import { MainLayoutContainer } from "components/Layout";
import { Add, FileUpload } from "@mui/icons-material";
import { REQUIREMENT, TABLE_ACTIONS } from "helpers/constants";
import { Link, useNavigate } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import { useRequirement } from "pages/Requirements/list/useRequirement";
import CustomTable from "components/Table/CustomTable";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import { ResponsiveButton, ResponsiveButtonLink } from "components/Button";
import { Link as LinkBase, Stack } from "@mui/material";
import { AddRequirementSelector } from "./components";
import { useAuthUser } from "hooks/useAuthUser";
import { ROLES } from "types/user";
import { DomainColorSquares } from "components/Requirements";
import { useMemo } from "react";
import { requirementsHaveDomainSwatches } from "utils/requirementDomains";

export const EPAList = () => {
  const navigate = useNavigate();
  const { role } = useAuthUser();
  const isAdmin = role === ROLES.ADMIN;
  const {
    allRequirements,
    loading,
    isOrgView,
    deleteRequirementOpen,
    organizationId,
    requirements,
    handleAddSelectedRequirement,
    handleActionClick,
    setDeleteRequirementOpen,
    handleDelete,
  } = useRequirement(REQUIREMENT.REQUIREMENT_EPA);

  const showDomainsColumn = useMemo(
    () => requirementsHaveDomainSwatches(requirements),
    [requirements],
  );

  return (
    <MainLayoutContainer
      title="Curriculum Requirements - EPAs"
      loading={loading}
      error={undefined}
      buttonActions={
        <Stack direction="row" spacing={2}>
          {isOrgView && (
            <AddRequirementSelector
              requirements={allRequirements}
              onSelect={(requirement) => {
                handleAddSelectedRequirement(requirement);
              }}
              selectedRequirement={requirements}
            />
          )}
          {isAdmin && (
            <ResponsiveButtonLink
              to={
                isOrgView && organizationId
                  ? AdminRoutes.OrganizationUploadSpreadsheetFramework.replace(
                      ":organizationId",
                      organizationId,
                    )
                  : AdminRoutes.UploadSpreadsheetFramework
              }
              icon={<FileUpload />}
            >
              Import spreadsheet
            </ResponsiveButtonLink>
          )}
          <ResponsiveButton
            onClick={() => {
              navigate(
                organizationId
                  ? `/organization/${organizationId}/epa/create`
                  : "/epa/create",
              );
            }}
            icon={<Add />}
          >
            Create New EPA
          </ResponsiveButton>
        </Stack>
      }
    >
      <CustomTable
        entities={requirements}
        handleActionClick={handleActionClick}
        fieldToFilter={(row: any, filterText: string) =>
          (row.name || "").toLowerCase().includes(filterText.toLowerCase()) ||
          (row.description || "")
            .toLowerCase()
            .includes(filterText.toLowerCase())
        }
        columns={[
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "description",
            label: "Requirement Description",
            value: (row: any) => {
              return row.description?.length > 128
                ? row.description.slice(0, 128)
                : row.description;
            },
          },
          ...(showDomainsColumn
            ? [
                {
                  id: "domains",
                  label: "Domains",
                  value: (row: any) => (
                    <DomainColorSquares requirement={row} />
                  ),
                },
              ]
            : []),
          {
            id: "requirement_id",
            value: (row: any) => (
              <Stack direction="row" spacing={2} alignItems={"center"}>
                <LinkBase
                  sx={{ cursor: "pointer" }}
                  component={Link}
                  to={
                    organizationId
                      ? `/organization/${organizationId}/epa/${row.requirement_id}`
                      : `/epa/${row.requirement_id}`
                  }
                >
                  View
                </LinkBase>
              </Stack>
            ),
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[
          TABLE_ACTIONS.VIEW,
          TABLE_ACTIONS.EDIT,
          TABLE_ACTIONS.DELETE,
        ]}
        extraActionItems={[]}
        idColumn={"requirement_id"}
        enableCardViewInSmallDevices={true}
      />
      {deleteRequirementOpen && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={`Are you sure you want to ${
            isOrgView ? "remove" : "delete"
          } this Requirement?`}
          show={deleteRequirementOpen}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteRequirementOpen(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
};
